import { Container, Grid, IconButton } from '@mui/material';
import { ReactNode, useState } from 'react';
import { Button, useGetList, usePermissions } from 'react-admin';
import { BiSolidTv, BiTv } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import constants from '../constants';
import CopyByDateOfBirthDialog from '../components/CopyByDateOfBirthDialog';
import ExpertsCard from './ExpertsCard';
import GroupSessionsCard from './GroupSessionsCard';
import MembersCard from './MembersCard';
import MoreInfoCard from './MoreInfoCard';
import NPSCard from './NPSCard';
import OnboardingNotesCard from './OnboardingNotesCard';
import PassItOnStatsCard from './PassItOnStatsCard';
import SettingsCard from './SettingsCard';
import useDashboard from './useDashboard';
import { ROLE } from '../providers/auth.types';
import axios from 'axios';

export const Dashboard = () => {
	const [showKidsDOBModal, setShowKidsDOBModal] = useState(false);
	const [isTVMode, setIsTVMode] = useState(false);
	const navigation = useNavigate();
	const { permissions } = usePermissions<ROLE>();
	// GH-735: member notes are not shown to the business manager
	const canSeeMemberNotes = permissions !== 'business-admin';
	const {
		handleChangeMyCompany,
		handleChangeMyExpert,
		handleCopyNewbornEmails,
		handleCopyNoWixEmails,
		activeNewborns,
		company,
		groupSessionsStats,
		newbornSentCount,
		expertId,
		expertStats,
		passItOnStats,
		randomOnboardingNotes,
		totalPrivateSessions,
		totalPrivateSessionsToday,
		memberStats,
		dating,
		memberDating,
		nps,
	} = useDashboard({ pollingInterval: isTVMode ? 30000 : null, skipMemberNotes: !canSeeMemberNotes });
	const companies = useGetList('company', {
		pagination: { perPage: 1000, page: 1 },
		sort: { field: 'english_name', order: 'ASC' },
	});
	const cards: { component: ReactNode; xs: number; sm: number; showInTvMode?: boolean; showInPCMode?: boolean }[] = [
		{
			xs: 12,
			sm: 4,
			component: (
				<SettingsCard
					company={company}
					companies={companies.data ?? []}
					expertId={expertId}
					expertStats={expertStats}
					handleChangeMyCompany={handleChangeMyCompany}
					handleChangeMyExpert={handleChangeMyExpert}
					handleCopyNoWixEmails={handleCopyNoWixEmails}
					handleCopyNewbornEmails={handleCopyNewbornEmails}
					activeNewborns={activeNewborns}
					setShowKidsDOBModal={setShowKidsDOBModal}
					memberDating={memberDating}
				/>
			),
			showInTvMode: false,
		},
		{
			xs: 12,
			sm: 4,
			component: (
				<div style={{ position: 'relative' }}>
					<div className="justify-center items-center flex absolute -top-10 ">
						<img src="transparent-logo.gif" style={{}} alt='logo'/>
					</div>
				</div>
			),
			showInTvMode: true,
			showInPCMode: false,
		},
		{
			xs: 12,
			sm: 4,
			component: (
				<MembersCard
					navigation={navigation}
					memberStats={memberStats}
					totalUsers={memberStats.data?.totalUsers}
					todayUsers={memberStats.data?.todayUsers}
					newUsers={memberStats.data?.newUsers}
					groupSessionsStatsLoading={groupSessionsStats.loading}
					todaysEnrollments={groupSessionsStats.data?.todaysEnrollments}
					totalPrivateSessionsToday={totalPrivateSessionsToday.data?.data}
					totalPrivateSessions={totalPrivateSessions?.data?.data}
				/>
			),
		},
		{ xs: 12, sm: 4, component: <NPSCard nps={nps} /> },
		{
			xs: 12,
			sm: 4,
			component: (
				<PassItOnStatsCard
					stats={passItOnStats.data?.stats}
					totalCount={passItOnStats.data?.totalCount}
					totalChange={passItOnStats.data?.totalChange}
				/>
			),
		},
		{
			xs: 12,
			sm: 8,
			showInPCMode: canSeeMemberNotes,
			showInTvMode: canSeeMemberNotes,
			component: (
				<OnboardingNotesCard
					isTVMode={isTVMode}
					data={randomOnboardingNotes.data}
					isFetching={randomOnboardingNotes.isFetching}
				/>
			),
		},
		{
			xs: 12,
			sm: 4,
			component: (
				<ExpertsCard
					loading={expertStats.loading}
					total={expertStats.data?.total}
					registeredExperts={expertStats.data?.registeredExperts}
					synchedExperts={expertStats.data?.synchedExperts}
					safeSpaceExperts={expertStats.data?.safeSpaceExperts}
				/>
			),
		},
		{
			xs: 12,
			sm: 4,
			component: (
				<MoreInfoCard
					title="נתונים נוספים"
					info={[
						{ title: 'מנויי לידה', value: newbornSentCount.data?.count },
						// GH-725 (Terra, 24.9.2026) — dating not in use; uncomment to bring back:
						// { title: 'רשומים לדייטינג בהייטק', value: dating.data },
						{ title: 'NPS', value: nps },
					]}
				/>
			),
		},

		{
			xs: 12,
			sm: 4,
			component: (
				<GroupSessionsCard
					loading={groupSessionsStats.loading}
					todaysSessions={groupSessionsStats.data?.todaysSessions}
					tomorrowSessions={groupSessionsStats.data?.tomorrowSessions}
				/>
			),
		},
	];
	return (
		<>
			<CopyByDateOfBirthDialog open={showKidsDOBModal} onDismiss={() => setShowKidsDOBModal(false)} />
			<div>
				<div className="absolute top-10 left-0 p-4">
					<IconButton
						color={isTVMode ? 'primary' : 'default'}
						title="מצב טלויזיה"
						onClick={() => {
							setIsTVMode(!isTVMode);
						}}
					>
						{isTVMode ? <BiSolidTv /> : <BiTv />}
					</IconButton>
				</div>
				
				<Container style={{ paddingTop: 30 }} maxWidth={isTVMode ? 'xl' : 'lg'}>
				<div className="flex justify-center items-center">
					<Button
						onClick={() => {
							const token = localStorage.getItem('token');
							if (!token) {
							
								return;
							}

							axios.post(`${constants.API_URL}setting/export-leads`, {
								headers: { Authorization: `Bearer ${token}` },
								responseType: 'blob'
							}).then(response => {
								// Create blob from response data
								const blob = new Blob([response.data], { type: 'text/csv' });
								
								// Create download link
								const url = window.URL.createObjectURL(blob);
								const link = document.createElement('a');
								link.href = url;
								link.download = 'leads.csv'; // Set filename
								link.style.display = 'none';
								
								// Trigger download
								document.body.appendChild(link);
								link.click();
								
								// Cleanup
								document.body.removeChild(link);
								window.URL.revokeObjectURL(url);
							}).catch(error => {
								console.error('Error downloading leads:', error);
							});
						}}
					>
						<span>נתוני הגרלה</span>
					</Button>
				



					<Button
						color='secondary'
						className='ml-2 bg-purple-500 hover:bg-purple-500 text-white rounded-md px-4 py-2'
						onClick={() => {
							const token = localStorage.getItem('token');
							if (!token) {
							
								return;
							}

							axios.post(`${constants.API_URL}setting/export-leads`, {
								headers: { Authorization: `Bearer ${token}` },
								responseType: 'blob',
								isDuplicate: false
							}).then(response => {
								// Create blob from response data
								const blob = new Blob([response.data], { type: 'text/csv' });
								
								// Create download link
								const url = window.URL.createObjectURL(blob);
								const link = document.createElement('a');
								link.href = url;
								link.download = 'leads.csv'; // Set filename
								link.style.display = 'none';
								
								// Trigger download
								document.body.appendChild(link);
								link.click();
								
								// Cleanup
								document.body.removeChild(link);
								window.URL.revokeObjectURL(url);
							}).catch(error => {
								console.error('Error downloading leads:', error);
							});
						}}
					>
						<span>נתוני הגרלה ללא כפולים</span>
					</Button>

					{/* GH-725 (Terra, 24.9.2026) — the two dating CSV exports ("נתוני דייטינג" / "נתוני דייטינג כללי")
					    are hidden because dating is no longer in use. The backend route admin/get-dates-info still
					    exists; to bring the buttons back, uncomment this block. */}
					{/*
					<Button
						className='ml-2'
						onClick={() => {
							const token = localStorage.getItem('token');
							if (!token) {
								return;
							}

							axios.get(`${constants.API_URL}admin/get-dates-info`, {
								headers: { Authorization: `Bearer ${token}` },
								responseType: 'blob'
							}).then(response => {
								// Create blob from response data
								const blob = new Blob([response.data], { type: 'text/csv' });
								
								// Create download link
								const url = window.URL.createObjectURL(blob);
								const link = document.createElement('a');
								link.href = url;
								link.download = 'dates-info.csv';
								link.style.display = 'none';
								
								// Trigger download
								document.body.appendChild(link);
								link.click();
								
								// Cleanup
								document.body.removeChild(link);
								window.URL.revokeObjectURL(url);
							}).catch(error => {
								console.error('Error downloading dates info:', error);
							});
						}}
					>
						<span>נתוני דייטינג</span>
					</Button>

					<Button
						className='ml-2'
						onClick={() => {
							const token = localStorage.getItem('token');
							if (!token) {
								return;
							}

							axios.get(`${constants.API_URL}admin/get-dates-info`, {
								headers: { Authorization: `Bearer ${token}` },
								responseType: 'blob',
								params: { removeFilter: true }
							}).then(response => {
								// Create blob from response data
								const blob = new Blob([response.data], { type: 'text/csv' });
								
								// Create download link
								const url = window.URL.createObjectURL(blob);
								const link = document.createElement('a');
								link.href = url;
								link.download = 'dates-info-general.csv';
								link.style.display = 'none';
								
								// Trigger download
								document.body.appendChild(link);
								link.click();
								
								// Cleanup
								document.body.removeChild(link);
								window.URL.revokeObjectURL(url);
							}).catch(error => {
								console.error('Error downloading dates info:', error);
							});
						}}
					>
						<span>נתוני דייטינג כללי</span>
					</Button>
					*/}
				</div>
					<Grid container spacing={2}>
					
						{cards
							.filter(
								(x) => (!isTVMode && x.showInPCMode !== false) || (isTVMode && x.showInTvMode !== false)
							)
							.map((card, index) => (
								<Grid
									item
									xs={card.xs}
									sm={card.sm}
									key={index}
									height={'100%'}
									style={{ height: 300, overflowY: 'scroll' }}
								>
									{card.component}
								</Grid>
							))}
					</Grid>
				</Container>
			</div>
		</>
	);
};
